import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../components/marketing/MarketingLayout';
import '../styles/marketing-homepage.css';

/* ============================================================
   Clarid homepage — "banker-paper, marketing-weight" redesign.
   Ported from the Claude Design handoff (Clarid Homepage.html +
   homepage.js). Ships the event-feed hero + pipeline moat; the
   review-only exploration switcher and the alternate hero/moat
   variants are intentionally dropped.
   ============================================================ */

type FeedStatus = 'finding' | 'review' | 'pass' | 'verified';
interface FeedItem {
  t: string;
  mod: string;
  txt: string;
  st: FeedStatus;
  stl: string;
  cascade?: string;
}

const FEED: FeedItem[] = [
  { t: '09:14:02', mod: 'Marketing', txt: 'APR disclosure missing on "Spring HELOC" email blast', st: 'finding', stl: 'FINDING', cascade: 'cascaded to → HMDA review queue' },
  { t: '09:14:05', mod: 'HMDA', txt: 'LAR row 1,284 · edit check V662 failed (Rate Spread)', st: 'review', stl: 'REVIEW' },
  { t: '09:14:09', mod: 'Fair Lending', txt: 'IFLEP · denial-rate disparity flagged by applicant geography', st: 'finding', stl: 'FINDING', cascade: 'cascaded to → Exam Package Q2' },
  { t: '09:14:11', mod: 'CRA', txt: 'Assessment-area lending gap detected · 2 census tracts', st: 'review', stl: 'REVIEW' },
  { t: '09:14:14', mod: 'Regression', txt: 'Pricing model · protected-class coefficient within tolerance', st: 'pass', stl: 'PASS' },
  { t: '09:14:18', mod: 'Exam Center', txt: 'Q2 examiner package rebuilt · 6 artifacts assembled', st: 'verified', stl: 'SHA-256·a1f9c2' },
  { t: '09:14:22', mod: 'Board Report', txt: 'Q2 fair-lending summary updated & timestamped', st: 'verified', stl: 'VERIFIED' },
];

interface Tick {
  mod: string;
  to: string[];
}
const TICKS: Tick[] = [
  { mod: 'Fair-lending finding', to: ['Exam Package Q2', 'Board Report Q2'] },
  { mod: 'HMDA edit-check fail', to: ['Fair Lending review', 'Exam Package Q2'] },
  { mod: 'Marketing APR gap', to: ['HMDA queue', 'Complaint watch'] },
  { mod: 'CRA assessment gap', to: ['Redlining screen', 'Board Report Q2'] },
];

function statusDot(s: FeedStatus): string {
  return s === 'finding' ? 'dot-burgundy' : s === 'review' ? 'dot-ochre' : s === 'pass' ? 'dot-forest' : 'dot-teal';
}

export default function LandingPage() {
  const feedRef = useRef<HTMLDivElement>(null);
  const moatRef = useRef<HTMLElement>(null);
  const pipeTrackRef = useRef<HTMLDivElement>(null);
  const pipeFillRef = useRef<HTMLDivElement>(null);
  const pipeTokenRef = useRef<HTMLDivElement>(null);
  const tickerBodyRef = useRef<HTMLDivElement>(null);

  /* ---------- Hero event feed (looping) ---------- */
  useEffect(() => {
    const feedEl = feedRef.current;
    if (!feedEl) return;
    // Treat small screens like reduced-motion: once the hero grid stacks (<=980px)
    // the feed/ticker go full-width, their rows wrap past the reserved height, and the
    // loop's clear/rebuild oscillates the page height — sections jump in and out of view
    // while reading. Freeze to the static complete state on mobile; desktop keeps the motion.
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce), (max-width: 980px)').matches;
    let timers: ReturnType<typeof setTimeout>[] = [];

    const buildRow = (item: FeedItem): HTMLDivElement => {
      const row = document.createElement('div');
      row.className = 'feed-row';
      const casc = item.cascade ? `<div class="feed-cascade"><span>↳</span>${item.cascade}</div>` : '';
      row.innerHTML =
        `<div class="feed-time">${item.t}</div>` +
        `<div class="feed-main"><div class="feed-mod">${item.mod}</div>` +
        `<div class="feed-txt">${item.txt}</div>${casc}</div>` +
        `<div class="feed-status st-${item.st}"><span class="dot ${statusDot(item.st)}"></span>${item.stl}</div>`;
      return row;
    };

    const run = () => {
      timers.forEach(clearTimeout);
      timers = [];
      feedEl.innerHTML = '';
      if (reduce) {
        FEED.forEach((item) => {
          const r = buildRow(item);
          r.classList.add('show');
          feedEl.appendChild(r);
        });
        return;
      }
      FEED.forEach((item, i) => {
        timers.push(
          setTimeout(() => {
            const r = buildRow(item);
            feedEl.appendChild(r);
            void r.offsetWidth;
            r.classList.add('show');
            while (feedEl.children.length > 7) feedEl.removeChild(feedEl.firstChild as Node);
          }, 700 + i * 850)
        );
      });
      timers.push(setTimeout(run, 700 + FEED.length * 850 + 3200));
    };

    run();
    return () => timers.forEach(clearTimeout);
  }, []);

  /* ---------- MOAT pipeline + cascade ticker (starts on scroll-in) ---------- */
  useEffect(() => {
    const track = pipeTrackRef.current;
    const moatSec = moatRef.current;
    if (!track || !moatSec) return;
    // Treat small screens like reduced-motion: once the hero grid stacks (<=980px)
    // the feed/ticker go full-width, their rows wrap past the reserved height, and the
    // loop's clear/rebuild oscillates the page height — sections jump in and out of view
    // while reading. Freeze to the static complete state on mobile; desktop keeps the motion.
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce), (max-width: 980px)').matches;
    const pnodes = Array.from(track.querySelectorAll<HTMLElement>('.pnode'));
    const pipeFill = pipeFillRef.current;
    const pipeToken = pipeTokenRef.current;
    const tickerBody = tickerBodyRef.current;
    let timers: ReturnType<typeof setTimeout>[] = [];

    const tickerLine = (item: Tick): HTMLDivElement => {
      const line = document.createElement('div');
      line.className = 'ticker-line';
      const arrows = item.to.map((t) => `<span class="arr">→</span> ${t}`).join(' ');
      line.innerHTML = `<span class="src">${item.mod}</span> · cascaded to ${arrows}`;
      return line;
    };

    const run = () => {
      timers.forEach(clearTimeout);
      timers = [];
      const step = 900;
      if (reduce) {
        pnodes.forEach((n) => n.classList.add('active'));
        if (pipeFill) pipeFill.style.width = '100%';
        if (tickerBody) {
          tickerBody.innerHTML = '';
          TICKS.forEach((it) => {
            const l = tickerLine(it);
            l.classList.add('show');
            tickerBody.appendChild(l);
          });
        }
        return;
      }
      pnodes.forEach((n) => n.classList.remove('active'));
      if (pipeFill) { pipeFill.style.transition = 'none'; pipeFill.style.width = '0'; }
      if (pipeToken) { pipeToken.style.transition = 'none'; pipeToken.style.left = '0'; pipeToken.style.opacity = '0'; }
      if (tickerBody) tickerBody.innerHTML = '';

      timers.push(
        setTimeout(() => {
          if (pipeToken) {
            pipeToken.style.transition = `left ${step * (pnodes.length - 1)}ms linear, opacity .3s`;
            pipeToken.style.opacity = '1';
            pipeToken.style.left = '100%';
          }
          if (pipeFill) {
            pipeFill.style.transition = `width ${step * (pnodes.length - 1)}ms linear`;
            pipeFill.style.width = '100%';
          }
        }, 120)
      );
      pnodes.forEach((n, i) => {
        timers.push(setTimeout(() => n.classList.add('active'), 160 + i * step));
      });
      TICKS.forEach((it, i) => {
        timers.push(
          setTimeout(() => {
            if (!tickerBody) return;
            const l = tickerLine(it);
            tickerBody.appendChild(l);
            void l.offsetWidth;
            l.classList.add('show');
            while (tickerBody.children.length > 4) tickerBody.removeChild(tickerBody.firstChild as Node);
          }, 600 + i * 700)
        );
      });
      if (pipeToken) timers.push(setTimeout(() => { pipeToken.style.opacity = '0'; }, step * (pnodes.length - 1) + 300));
      timers.push(setTimeout(run, step * pnodes.length + 3600));
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            run();
            io.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(moatSec);

    return () => {
      io.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <MarketingLayout activeNav="platform">
      {/* ============ A · HERO ============ */}
      <section className="hero">
        <div className="container hero-grid">
          <div>
            <div className="kicker" style={{ marginBottom: '1.6rem' }}>§&nbsp;00 · COMPLIANCE OPERATING SYSTEM</div>
            <h1 className="display balance">Walk into your exam<br />already done.</h1>
            <p className="lead pretty" style={{ marginTop: '1.6rem', maxWidth: '34ch' }}>
              The compliance operating system for community banks and credit unions —
              marketing, HMDA, fair lending, CRA, and exam packaging in one connected platform.{' '}
              <span style={{ color: 'var(--ink)' }}>Deterministic, examiner-grade, built by bankers and former examiners.</span>
            </p>
            <div className="hero-cta">
              <Link to="/pricing" className="btn btn-primary">See Pricing <span className="arrow">→</span></Link>
              <Link to="/request-demo" className="btn btn-secondary">Request a demo</Link>
            </div>
            <div className="hero-cred" style={{ marginTop: '2.4rem' }}>
              <span className="meta">Built by a community banker</span>
              <span className="meta" style={{ color: 'var(--rule)' }}>·</span>
              <span className="meta">Validated by former FDIC and Federal Reserve examiners</span>
            </div>
          </div>

          {/* Hero visual: live compliance event feed */}
          <div>
            <div className="viz">
              <div className="viz-bar">
                <span className="meta">COMPLIANCE_EVENT_FEED · clarid.os</span>
                <span className="live"><span className="dot dot-teal"></span> LIVE</span>
              </div>
              <div className="feed" ref={feedRef}>{/* rows injected by effect */}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ B · PROBLEM ============ */}
      <section className="section panel-bg hairline-top">
        <div className="container">
          <div className="sign reveal" style={{ marginBottom: 'clamp(2.5rem,4vw,3.5rem)' }}>
            <div className="sign-row">
              <span className="kicker kicker--ink">§&nbsp;01 · THE PROBLEM</span>
              <hr className="rule" />
            </div>
            <h2 className="h2 balance" style={{ maxWidth: '20ch' }}>Compliance is scattered, reactive, and dreaded.</h2>
          </div>
          <div className="beats reveal">
            <div className="beat">
              <span className="beat-no">01</span>
              <h3 className="h3">Disconnected point tools</h3>
              <p className="body">Five point tools that don't talk to each other. Findings die in silos instead of informing the next review.</p>
            </div>
            <div className="beat">
              <span className="beat-no">02</span>
              <h3 className="h3">The three-week exam scramble</h3>
              <p className="body">Every exam means re-pulling data, rebuilding spreadsheets, and chasing approvals — work you already did, done again under pressure.</p>
            </div>
            <div className="beat">
              <span className="beat-no">03</span>
              <h3 className="h3">Regulatory blind spots</h3>
              <p className="body">A marketing slip becomes a fair-lending issue becomes an exam finding — and nobody connected the dots until the examiner did.</p>
            </div>
          </div>
          <p className="lead reveal" style={{ marginTop: 'clamp(2rem,3.5vw,3rem)', maxWidth: '46ch' }}>
            Clarid solves all three — <span style={{ color: 'var(--ink)' }}>in one platform that connects them.</span>
          </p>
        </div>
      </section>

      {/* ============ C · THE MOAT ============ */}
      <section className="section moat" id="moat" ref={moatRef}>
        <div className="container">
          <div className="sign reveal" style={{ marginBottom: '1.6rem' }}>
            <div className="sign-row">
              <span className="kicker">§&nbsp;02 · THE MOAT</span>
              <hr className="rule" />
            </div>
            <h2 className="h2 balance" style={{ maxWidth: '18ch' }}>Every module makes the others smarter.</h2>
          </div>
          <p className="body reveal" style={{ maxWidth: '60ch', fontSize: '1.15rem' }}>
            Everywhere else, these stay in silos. A finding in one module surfaces an insight in another —
            and all of it rolls into one examiner-ready package. <span style={{ color: 'var(--paper)' }}>Nobody else connects compliance like this.</span>
          </p>

          <div className="pipeline reveal">
            <div className="pipe-track" ref={pipeTrackRef}>
              <div className="pipe-line"></div>
              <div className="pipe-fill" ref={pipeFillRef}></div>
              <div className="pipe-token" ref={pipeTokenRef}></div>
              <div className="pnode"><span className="pdot"></span><span className="pname">Marketing</span></div>
              <div className="pnode"><span className="pdot"></span><span className="pname">HMDA</span></div>
              <div className="pnode"><span className="pdot"></span><span className="pname">Fair&nbsp;Lending</span></div>
              <div className="pnode"><span className="pdot"></span><span className="pname">Redlining</span></div>
              <div className="pnode"><span className="pdot"></span><span className="pname">Regression</span></div>
              <div className="pnode"><span className="pdot"></span><span className="pname">Exam&nbsp;&amp;&nbsp;Board</span></div>
            </div>
          </div>
          <div className="ticker reveal">
            <div className="ticker-head">Cascade log · live</div>
            <div ref={tickerBodyRef}>{/* lines injected */}</div>
          </div>
        </div>
      </section>

      {/* ============ D · OUTCOMES ============ */}
      <section className="section" id="outcomes">
        <div className="container">
          <div className="sign reveal" style={{ marginBottom: 'clamp(2.5rem,4vw,3.5rem)' }}>
            <div className="sign-row">
              <span className="kicker kicker--ink">§&nbsp;03 · WHAT YOU GET</span>
              <hr className="rule" />
            </div>
            <h2 className="h2 balance" style={{ maxWidth: '20ch' }}>Catch it early. Package it once. Prove every check.</h2>
          </div>
          <div className="pillars reveal">
            <div className="pillar card">
              <span className="pillar-no">01</span>
              <h3>Catch problems before the examiner does</h3>
              <div className="pillar-mods">
                <span className="chip">Fair lending · IFLEP</span>
                <span className="chip">HMDA</span>
                <span className="chip">CRA</span>
                <span className="chip">Regression</span>
                <span className="chip">Whole-loan-book</span>
              </div>
              <p className="body">Seventeen fair-lending risk factors, 170+ HMDA edit checks, CRA assessment-area analysis, pricing regression — now extended to non-HMDA, whole-loan-book lending.</p>
              <div className="pillar-proof">
                <span className="stat-num">FRB Fair Lending Tool</span>
                <span className="meta">reproduces the methodology to the decimal</span>
              </div>
            </div>
            <div className="pillar card">
              <span className="pillar-no">02</span>
              <h3>Never scramble for an exam again</h3>
              <div className="pillar-mods">
                <span className="chip">Exam readiness scoring</span>
                <span className="chip">One-click package</span>
              </div>
              <p className="body">A continuously-scored readiness picture that compiles into a complete examiner package — every artifact, every workpaper, assembled and current.</p>
              <div className="pillar-proof">
                <span className="stat-num">Examiner-ready ZIP</span>
                <span className="meta">complete package · one click</span>
              </div>
            </div>
            <div className="pillar card">
              <span className="pillar-no">03</span>
              <h3>Prove it — every check, timestamped</h3>
              <div className="pillar-mods">
                <span className="chip">Immutable audit trail</span>
                <span className="chip">Board reports</span>
                <span className="chip">Verified exports</span>
              </div>
              <p className="body">Every check, every export, every board report carries a timestamp and a cryptographic hash. Nothing to reconstruct, nothing to dispute.</p>
              <div className="pillar-proof">
                <span className="stat-num">SHA-256 verified</span>
                <span className="meta">examiner-defensible by construction</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ D2 · THE WHOLE LOAN BOOK ============ */}
      <section className="section">
        <div className="container">
          <div className="sign reveal" style={{ marginBottom: 'clamp(2.5rem,4vw,3.5rem)' }}>
            <div className="sign-row">
              <span className="kicker kicker--ink">§&nbsp;04 · THE WHOLE LOAN BOOK</span>
              <hr className="rule" />
            </div>
            <h2 className="h2 balance" style={{ maxWidth: '24ch' }}>Fair lending and CRA on your whole loan book &mdash; not just mortgages.</h2>
          </div>
          <div className="wlb-grid reveal">
            <div>
              <p className="lead">
                Most fair-lending tools stop at the HMDA LAR. Clarid runs the same analysis on the
                loans that never reach a LAR &mdash; consumer, small business, and small farm &mdash;
                straight from your loan trial balance (the ALERT&nbsp;/&nbsp;ILDR file). That&rsquo;s the
                book community banks and ISBs could never analyze, because there&rsquo;s no HMDA
                equivalent for it.
              </p>
              <ul className="wlb-points">
                <li>Proxied sex &amp; ethnicity (FRB and FDIC methodologies) on consumer, small-business, and small-farm loans</li>
                <li>Feeds the same redlining, CRA, and exam-packaging surfaces as your HMDA data</li>
                <li>SSN dropped on ingest; names and addresses encrypted at rest</li>
              </ul>
            </div>
            <div className="wlb-proofs">
              <div className="wlb-proof">
                <span className="stat-num">FRB Fair Lending Tool</span>
                <span className="meta">reproduced to the decimal &mdash; now on the non-HMDA book</span>
              </div>
              <div className="wlb-proof">
                <span className="stat-num">6 hours &rarr; 30 seconds</span>
                <span className="meta">the manual ALERT-file Excel grind, automated</span>
              </div>
              <div className="wlb-proof">
                <span className="stat-num">Small banks &amp; ISBs</span>
                <span className="meta">the institutions HMDA-only tools can&rsquo;t serve</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ E · DETERMINISTIC ============ */}
      <section className="section panel-bg hairline-top hairline-bottom" id="platform">
        <div className="container">
          <div className="det-grid">
            <div className="reveal">
              <div className="sign-row" style={{ marginBottom: '1.6rem' }}>
                <span className="kicker kicker--ink">§&nbsp;05 · DETERMINISTIC, NOT A WRAPPER</span>
              </div>
              <h2 className="h2 balance" style={{ maxWidth: '18ch' }}>Compliance you can defend — not a ChatGPT wrapper.</h2>
              <p className="body" style={{ marginTop: '1.5rem', maxWidth: '48ch', fontSize: '1.1rem' }}>
                Every vendor says "AI-powered." Determinism is the differentiator. Clarid runs multi-provider
                AI with circuit-breaker fallback — when one provider fails, the next takes over without
                changing the answer.
              </p>
              <p className="lead" style={{ marginTop: '1.5rem', maxWidth: '42ch' }}>
                When an AI provider goes down, <span style={{ color: 'var(--ink)' }}>your compliance doesn't.</span>
              </p>
              <p className="meta" style={{ marginTop: '1rem', color: 'var(--ink-4)' }}>Proven in production · stayed up through Anthropic's largest-ever outage</p>
            </div>
            <div className="reveal">
              <div className="fallback">
                <div className="fb-row"><span className="fb-idx">01</span><span className="fb-name">OpenAI</span><span className="fb-tag" style={{ color: 'var(--forest)' }}>PRIMARY · ACTIVE</span></div>
                <div className="fb-row"><span className="fb-idx">02</span><span className="fb-name">Anthropic</span><span className="fb-tag" style={{ color: 'var(--ochre)' }}>FALLBACK · STANDBY</span></div>
                <div className="fb-row"><span className="fb-idx">03</span><span className="fb-name">Google</span><span className="fb-tag" style={{ color: 'var(--ink-4)' }}>FALLBACK · STANDBY</span></div>
                <div className="fb-row" style={{ background: 'var(--paper)' }}><span className="fb-idx" style={{ color: 'var(--teal)' }}>→</span><span className="fb-name" style={{ color: 'var(--ink-3)', fontSize: '0.74rem' }}>Circuit-breaker · deterministic output preserved across providers</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ F · ROI ============ */}
      <section className="section">
        <div className="container">
          <div className="sign reveal" style={{ marginBottom: 'clamp(2.5rem,4vw,3.5rem)' }}>
            <div className="sign-row">
              <span className="kicker kicker--ink">§&nbsp;06 · ONE PLATFORM, NOT A STACK</span>
              <hr className="rule" />
            </div>
            <h2 className="h2 balance" style={{ maxWidth: '20ch' }}>One platform does the work of five.</h2>
          </div>
          <div className="roi-grid reveal">
            <div className="vendors">
              <div className="vendor"><span>Ncontracts</span><span className="vlabel">vendor</span></div>
              <div className="vendor"><span>Wolters · fair lending</span><span className="vlabel">vendor</span></div>
              <div className="vendor"><span>CRA Wiz</span><span className="vlabel">vendor</span></div>
              <div className="vendor"><span>Marketing-review tool</span><span className="vlabel">vendor</span></div>
              <div className="vendor"><span>Exam-prep hours</span><span className="vlabel">internal</span></div>
            </div>
            <div className="roi-arrow">→</div>
            <div className="roi-clarid">
              <div className="kicker" style={{ marginBottom: '1rem' }}>CLARID</div>
              <p className="stat-num" style={{ fontSize: '1.6rem', color: 'var(--ink)', letterSpacing: '-0.02em' }}>$45K–$200K/yr</p>
              <p className="meta" style={{ margin: '0.4rem 0 1.2rem' }}>typical community-bank spend, replaced</p>
              <p className="body">Clarid replaces the tools and the manual hours — and connects them, so a finding in one module surfaces an insight in another.</p>
            </div>
          </div>
          <p className="lead reveal" style={{ marginTop: 'clamp(2rem,3.5vw,3rem)', maxWidth: '62ch', paddingTop: 'clamp(1.5rem,2.5vw,2rem)', borderTop: '1px solid var(--rule)' }}>
            Clarid is the engine community banks <span style={{ color: 'var(--ink)' }}>and their advisors</span> run on — it does the analytical grunt work so compliance teams and consultants spend their time on judgment, not spreadsheets.
          </p>
        </div>
      </section>

      {/* ============ G · TEAM ============ */}
      <section className="section panel-bg hairline-top">
        <div className="container">
          <div className="sign reveal" style={{ marginBottom: 'clamp(2.5rem,4vw,3.5rem)' }}>
            <div className="sign-row">
              <span className="kicker kicker--ink">§&nbsp;07 · WHO BUILT IT</span>
              <hr className="rule" />
            </div>
            <h2 className="h2 balance" style={{ maxWidth: '24ch' }}>Built by bankers, validated by examiners — in-house, not advisors.</h2>
          </div>
          <div className="team reveal">
            <div className="card team-card">
              <span className="role">Community banker</span>
              <h3 className="h3">A bank CMO who lived marketing compliance</h3>
              <p className="body">Built from inside a community bank's marketing function — the real workflows, the real review burden, the real exam questions.</p>
            </div>
            <div className="card team-card">
              <span className="role">Former examiners</span>
              <h3 className="h3">Former FDIC &amp; Federal Reserve examiners on staff</h3>
              <p className="body">FLE-credentialed former examiners — including 30 years at the Federal Reserve in fair lending — not an advisory board, full-time on the product.</p>
            </div>
            <div className="card team-card">
              <span className="role">Depth, not headcount</span>
              <h3 className="h3">The practitioners behind the AI</h3>
              <p className="body">The methodology comes from people who ran the exams. The AI executes it deterministically, at scale, every day.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ H · MCP ============ */}
      <section className="section">
        <div className="container">
          <div className="det-grid">
            <div className="reveal">
              <div className="sign-row" style={{ marginBottom: '1.6rem' }}>
                <span className="kicker kicker--ink">§&nbsp;08 · MODEL CONTEXT PROTOCOL</span>
              </div>
              <h2 className="h2 balance" style={{ maxWidth: '20ch' }}>Works inside the AI tools your team already uses.</h2>
              <p className="body" style={{ marginTop: '1.5rem', maxWidth: '46ch', fontSize: '1.1rem' }}>
                Clarid exposes its compliance tools over the Model Context Protocol — so your team can query
                straight from Claude, ChatGPT, or Copilot, scoped to your plan and your data.
              </p>
              <div className="mcp-tools">
                <span className="chip">Claude</span>
                <span className="chip">ChatGPT</span>
                <span className="chip">Copilot</span>
              </div>
            </div>
            <div className="reveal mcp-queries">
              <div className="q"><span className="prompt">›</span> Which HMDA LAR rows failed edit checks this quarter?</div>
              <div className="q"><span className="prompt">›</span> Show fair-lending denial disparities by assessment area.</div>
              <div className="q"><span className="prompt">›</span> Build the Q2 examiner package and verify every hash.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ I · PRICING TEASER ============ */}
      <section className="section panel-bg hairline-top hairline-bottom">
        <div className="container">
          <div className="sign reveal" style={{ marginBottom: 'clamp(2.5rem,4vw,3.5rem)' }}>
            <div className="sign-row">
              <span className="kicker kicker--ink">§&nbsp;09 · PLANS &amp; FEATURES</span>
              <hr className="rule" />
            </div>
            <h2 className="h2 balance" style={{ maxWidth: '18ch' }}>Two plans, sized to your institution.</h2>
          </div>
          <div className="tiers reveal">
            <div className="tier">
              <div className="tier-name">Professional</div>
              <p className="tier-who">For community banks building a connected compliance program.</p>
              <div className="tier-price">Let's talk</div>
              <ul className="tier-list">
                <li><span className="ck">✓</span> Marketing compliance</li>
                <li><span className="ck">✓</span> HMDA basics · LAR validation, 170+ edit checks</li>
                <li><span className="ck">✓</span> Regulatory monitoring</li>
                <li><span className="ck">✓</span> Exam readiness</li>
                <li><span className="ck">✓</span> Compliance program management</li>
              </ul>
              <Link to="/pricing" className="btn btn-secondary">Schedule a conversation</Link>
            </div>
            <div className="tier tier--feat">
              <span className="tier-flag">Most connected</span>
              <div className="tier-name">Enterprise</div>
              <p className="tier-who">Everything in Professional, plus the full cross-module suite.</p>
              <div className="tier-price">Let's talk</div>
              <ul className="tier-list">
                <li><span className="ck">✓</span> Full HMDA suite · geocoding, APOR, CFPB filing</li>
                <li><span className="ck">✓</span> Fair lending · IFLEP, 17 risk factors</li>
                <li><span className="ck">✓</span> CRA · regression analysis</li>
                <li><span className="ck">✓</span> Enforcement intelligence · full exam center</li>
                <li><span className="ck">✓</span> API · SSO/SCIM · MCP · whole-loan-book (ALERT/ILDR)</li>
              </ul>
              <Link to="/pricing" className="btn btn-primary">Schedule a conversation</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ J · FINAL CTA ============ */}
      <section className="section final" id="demo">
        <div className="container">
          <div className="reveal" style={{ maxWidth: '42ch' }}>
            <div className="kicker" style={{ color: 'var(--teal-bright)', marginBottom: '1.6rem' }}>§&nbsp;10 · GET STARTED</div>
            <h2 className="h2 balance">Stop managing compliance with spreadsheets and silos.</h2>
            <p className="lead" style={{ color: 'rgba(243,241,234,0.7)', marginTop: '1.5rem' }}>
              One connected platform. Every audit trail. Ready for your next exam.
            </p>
            <div className="final-cta">
              <Link to="/pricing" className="btn btn-on-ink">See Pricing <span className="arrow">→</span></Link>
              <Link to="/request-demo" className="btn btn-on-ink-ghost">Request a demo</Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
